import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Button } from '@app/components';
import { Form, Modal } from 'react-bootstrap';
import { bulkUpdateSubjectRate } from '@app/services/franchise';

const BulkUpdateRateTypeModal = (props) => {

    const [isLoading, setLoading] = useState(false);
    const [subjectRates, setSubjectRates] = useState({});
    const [rateErrors, setRateErrors] = useState({});

    // Get active subjects
    const activeSubjects = props.subjects?.filter(s => s.status === 'active') || [];

    useEffect(() => {
        // Initialize rates for each subject with their current rate when modal opens
        if(props.show && props.subjects?.length > 0) {
            const initialRates = {};
            const currentRateType = activeSubjects.length > 0 ? (activeSubjects[0].rate_type || 'fixed') : 'fixed';
            
            props.subjects.forEach(subject => {
                if(subject.status === 'active') {
                    initialRates[subject.subject_id] = subject.rate || 0;
                }
            });
            setSubjectRates(initialRates);
            setRateErrors({});
            
            // Set the current rate_type in the form
            setFieldValue('rate_type', currentRateType);
        }
    }, [props.show, props.subjects]);

    const bulkUpdateAPI = async (payload) => {
        try {
            setLoading(true);
            const resp = await bulkUpdateSubjectRate(payload);
            if(resp.success === true){
                setLoading(false);
                toast.success(resp?.message);
                props.handleClose();
                props.getSubjectsByFranchiseId(props?.franchiseId);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.response?.message || 'Failed');
        }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
        initialValues: {
            franchise_id: props?.franchiseId ?? '',
            rate_type: activeSubjects.length > 0 ? (activeSubjects[0].rate_type || 'fixed') : 'fixed',
            is_default: false,
        },
        validationSchema: Yup.object({
            rate_type: Yup.string().required('Please select rate type'),
        }),
        onSubmit: (values) => {
            if(activeSubjects.length === 0) {
                toast.error('No active subjects to update');
                return;
            }

            // Validate all active subject rates
            let hasError = false;
            const errors = {};
            const maxRate = values.rate_type === 'percentage' ? 100 : 10000;

            activeSubjects.forEach(subject => {
                const rate = subjectRates[subject.subject_id];
                if(rate === '' || rate === null || rate === undefined) {
                    errors[subject.subject_id] = 'Please enter rate';
                    hasError = true;
                } else if(parseFloat(rate) < 0) {
                    errors[subject.subject_id] = 'Rate must be ≥ 0';
                    hasError = true;
                } else if(parseFloat(rate) > maxRate) {
                    errors[subject.subject_id] = values.rate_type === 'percentage' 
                        ? 'Must be ≤ 100'
                        : 'Must be ≤ 10,000';
                    hasError = true;
                }
            });

            if(hasError) {
                setRateErrors(errors);
                toast.error('Please fix rate errors');
                return;
            }

            // Build subjects array with individual rates for ALL active subjects
            const subjects = activeSubjects.map(subject => ({
                subject_id: subject.subject_id,
                rate: parseFloat(subjectRates[subject.subject_id]),
                rate_type: values.rate_type,
                is_default: values.rate_type === 'fixed' ? values.is_default : false
            }));

            const payload = {
                franchise_id: values.franchise_id,
                subjects: subjects
            };

            bulkUpdateAPI(payload);
        },
        enableReinitialize: true,
    });

    const handleRateTypeChange = (e) => {
        const newRateType = e.target.value;
        setFieldValue('rate_type', newRateType);
        setRateErrors({}); // Clear errors when changing type
        if(newRateType === 'percentage') {
            setFieldValue('is_default', false);
        }
    };

    const handleDefaultRateChange = (e) => {
        const isChecked = e.target.checked;
        setFieldValue('is_default', isChecked);
        
        if(isChecked) {
            // Auto-fill all active subjects with their default rate
            const defaultRates = {};
            props.subjects.forEach(subject => {
                if(subject.status === 'active' && subject.default_rate) {
                    defaultRates[subject.subject_id] = subject.default_rate;
                }
            });
            setSubjectRates(prev => ({
                ...prev,
                ...defaultRates
            }));
        }
    };

    const handleRateChange = (subjectId, value) => {
        setSubjectRates(prev => ({
            ...prev,
            [subjectId]: value
        }));
        // Clear error for this subject
        if(rateErrors[subjectId]) {
            setRateErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[subjectId];
                return newErrors;
            });
        }
        // Uncheck "Set Default Rate" if user manually changes any rate
        if(values.is_default) {
            setFieldValue('is_default', false);
        }
    };

    return (
    <div>
        <Modal show={props.show} onHide={props.handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Update Rate Type & Rates
                    {activeSubjects.length > 0 && (
                        <small className="text-muted ml-2" style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                            (Current: {activeSubjects[0].rate_type === 'fixed' ? 'Fixed (£)' : 'Percentage (%)'})
                        </small>
                    )}
                </Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className='row'>
                        <div className="col-sm-12 mb-3">
                            <div className="alert alert-info">
                                <i className="fas fa-info-circle mr-2"></i>
                                <strong>Note:</strong> Use this to change the rate type or bulk update rates for <b>all {activeSubjects.length} active subject(s)</b>.
                            </div>
                        </div>

                        <div className="col-sm-6">
                            <div className="form-group">
                                <label htmlFor="rate_type">Rate Type<span className="required">*</span></label>
                                <select
                                    className="form-control"
                                    name="rate_type"
                                    value={values.rate_type}
                                    onChange={handleRateTypeChange}
                                >
                                    <option value="fixed">Fixed (£)</option>
                                    <option value="percentage">Percentage (%)</option>
                                </select>
                                {(touched.rate_type && errors.rate_type) && (
                                    <div className="text-danger">
                                        {errors.rate_type}
                                    </div>
                                )}
                            </div>
                        </div>

                        {values.rate_type === 'fixed' && (
                            <div className="col-sm-6">
                                <div className="form-group">
                                    <label>&nbsp;</label>
                                    <div className="form-check" style={{paddingTop: '8px'}}>
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input" 
                                            checked={values.is_default} 
                                            name="is_default" 
                                            id="is_default"
                                            onChange={handleDefaultRateChange}
                                        />
                                        <label className="form-check-label" htmlFor="is_default">
                                            Set Default Rate
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="col-sm-12">
                            <hr />
                            <label><b>Active Subjects ({activeSubjects.length}):</b></label>
                        </div>

                        <div className="col-sm-12">
                            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="table table-bordered table-sm">
                                    <thead className="thead-light" style={{position: 'sticky', top: 0, zIndex: 1}}>
                                        <tr>
                                            <th>Subject</th>
                                            <th width="150">Current</th>
                                            <th width="200">{values.rate_type === 'percentage' ? 'Percentage Rate' : 'Rate Per Student'}<span className="required">*</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeSubjects.map((subject) => (
                                            <tr key={subject.subject_id}>
                                                <td><b>{subject.name}</b></td>
                                                <td className="text-muted small">
                                                    {subject.rate_type === 'percentage' ? `${subject.rate}%` : `£${subject.rate}`}
                                                </td>
                                                <td>
                                                    <div className='input-group input-group-sm'>
                                                        {values.rate_type === 'fixed' && (
                                                            <div className="input-group-prepend">
                                                                <span className="input-group-text">£</span>
                                                            </div>
                                                        )}
                                                        <input 
                                                            type="number" 
                                                            className={`form-control form-control-sm ${rateErrors[subject.subject_id] ? 'is-invalid' : ''}`}
                                                            placeholder={values.rate_type === 'percentage' ? 'Enter percentage' : 'Enter rate'} 
                                                            value={subjectRates[subject.subject_id] || ''}
                                                            onChange={(e) => handleRateChange(subject.subject_id, e.target.value)}
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                            step="0.01"
                                                            disabled={values.is_default}
                                                        />
                                                        {values.rate_type === 'percentage' && (
                                                            <div className="input-group-append">
                                                                <span className="input-group-text">%</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {rateErrors[subject.subject_id] && (
                                                        <small className="text-danger">{rateErrors[subject.subject_id]}</small>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {activeSubjects.length === 0 && (
                                            <tr>
                                                <td colSpan="3" className="text-center text-danger">
                                                    No active subjects available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2" disabled={activeSubjects.length === 0}>
                        Update All {activeSubjects.length} Subject(s)
                    </Button>
                    <Button className='btn btn-default' onClick={props.handleClose}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    </div>
  )
}

export default BulkUpdateRateTypeModal;
